import { DfSelectSingleOption } from '../../../shared/_directives/df-select-single/df-select-single-filter.model';

export interface IrsXmlCompareRow {
  submissionId?: number | string;
  entity?: string;
  entitySub?: string;
  entityType?: string;
  form?: string;
  formSub?: string;
  parentElementName?: string;
  elementName?: string;
  sno?: number | string;
  source1ElementValue?: string;
  source2ElementValue?: string;
  changeStatus?: string;
}

export interface IrsXmlCompareApiResponse {
  callSuccess?: string;
  errorMessage?: string;
  data?: IrsXmlCompareRow[];
}

export interface XmlCompareExportHeader {
  object_id: string;
  label: string;
  data_type: 'string' | 'number';
  format: string;
  map: string;
  width: number;
}

const XML_COMPARE_COLUMNS: Array<{
  label: string;
  map: keyof IrsXmlCompareRow | 'row_num';
  width: number;
  dataType: 'string' | 'number';
}> = [
  // gizmoGrid renders row numbers in the first fixed column (`.div-col-prime`).
  { label: '', map: 'row_num', width: 42, dataType: 'number' },
  { label: 'Submission ID', map: 'submissionId', width: 110, dataType: 'number' },
  { label: 'Entity', map: 'entity', width: 90, dataType: 'string' },
  { label: 'Entity Sub', map: 'entitySub', width: 100, dataType: 'string' },
  { label: 'Entity Type', map: 'entityType', width: 100, dataType: 'string' },
  { label: 'Form', map: 'form', width: 160, dataType: 'string' },
  { label: 'Form Sub', map: 'formSub', width: 90, dataType: 'string' },
  { label: 'Parent Element', map: 'parentElementName', width: 140, dataType: 'string' },
  { label: 'Element Name', map: 'elementName', width: 320, dataType: 'string' },
  { label: 'SNO', map: 'sno', width: 70, dataType: 'number' },
  { label: 'Source 1 Element Value', map: 'source1ElementValue', width: 220, dataType: 'string' },
  { label: 'Source 2 Element Value', map: 'source2ElementValue', width: 220, dataType: 'string' },
  { label: 'Change Status', map: 'changeStatus', width: 120, dataType: 'string' },
];

/** Build a gizmoGrid-compatible payload from `compareIrsXml` API rows. */
export function buildXmlCompareGridPayload(rows: IrsXmlCompareRow[]): Record<string, unknown> {
  const header = XML_COMPARE_COLUMNS.map((col, index) => ({
    object_id: String(index + 1),
    label: col.label,
    'full-label': col.label,
    map: col.map,
    data_type: col.dataType,
    edit_type: col.dataType === 'number' ? 'number' : 'text',
    format: '',
    validation: '',
    editable: 'n',
    display: 'y',
    width: col.width,
    header_class: '',
    col_class: '',
  }));

  const data = (Array.isArray(rows) ? rows : []).map((row, index) => ({
    ...row,
    object_id: String(index + 1),
    isFiltered: 'N',
  }));

  return {
    callSuccess: '1',
    header,
    data,
    settings: {
      grid_id: 'efile-xml-compare',
      grid_type: 'standard',
      header_height: 30,
      fixed_columns: 1,
      batch_select_rules: '',
      comments: 0,
      attachments: 0,
    },
    columnTemplates: [],
    actionTemplates: [],
    batchSelect: [],
    firstLoad: 1,
  };
}

/**
 * Export headers for `/exportToExcel` filtered mode.
 * Row-number helper column is intentionally excluded.
 */
export function buildXmlCompareExportHeaders(): XmlCompareExportHeader[] {
  return XML_COMPARE_COLUMNS
    .filter((col) => col.map !== 'row_num')
    .map((col, index) => ({
      object_id: String(index + 1),
      label: col.label,
      data_type: col.dataType,
      format: '',
      map: String(col.map),
      width: col.width,
    }));
}

/** Proportionally widen scrollable columns when their total width is less than the host. */
export function stretchGridHeadersToHostWidth(
  headers: Array<{ width?: number | string }>,
  fixedColumns: number,
  hostWidth: number,
  vtrackWidth = 14,
): Array<{ width?: number | string }> | null {
  if (!Array.isArray(headers) || headers.length === 0 || hostWidth <= 0) {
    return null;
  }

  const fixed = Math.max(0, Math.min(fixedColumns, headers.length));
  let fixedTotal = 0;
  let scrollableTotal = 0;

  for (let i = 0; i < headers.length; i++) {
    const w = parseFloat(String(headers[i]?.width)) || 100;
    if (i < fixed) {
      fixedTotal += w;
    } else {
      scrollableTotal += w;
    }
  }

  const available = hostWidth - fixedTotal - vtrackWidth;
  if (scrollableTotal <= 0 || available <= scrollableTotal + 2) {
    return null;
  }

  const extra = available - scrollableTotal;
  const stretched = headers.map((header, index) => {
    if (index < fixed) {
      return header;
    }
    const w = parseFloat(String(header.width)) || 100;
    return {
      ...header,
      width: w + Math.round((extra * w) / scrollableTotal),
    };
  });

  // Absorb rounding remainder on the last scrollable column.
  let scrollSum = 0;
  for (let i = fixed; i < stretched.length; i++) {
    scrollSum += parseFloat(String(stretched[i].width)) || 0;
  }
  const drift = available - scrollSum;
  if (drift !== 0) {
    const last = stretched.length - 1;
    const lastW = parseFloat(String(stretched[last].width)) || 0;
    stretched[last] = { ...stretched[last], width: lastW + drift };
  }

  return stretched;
}

export function selectedIrsId(filter: { selected?: { VALUE?: string | number }[] } | null): string {
  const selected = filter?.selected;
  if (!Array.isArray(selected) || selected.length === 0) {
    return '';
  }
  const value = selected[0]?.VALUE;
  return value !== undefined && value !== null ? String(value).trim() : '';
}

/** Read `irs_id` / `irs_id_2` from Return Review tab-level filters. */
export function irsIdFromFilterList(
  filters: Array<{ param_name?: string; selected?: { VALUE?: string | number }[] }>,
  paramName: string,
): string {
  const match = filters.find((f) => f.param_name === paramName);
  return selectedIrsId(match || null);
}
